import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Spacing from 'components/Spacing';
import { Button } from 'components/Button';
import { Footer } from 'components/Footer';
import { Header } from 'components/Header';
import Icon, { IconNames } from 'components/Icon';
import Loader from 'components/Loader';
import { RowToGrid } from 'components/Row';
import { clearCartCuadros, getCartCuadros, setCartCuadros } from 'services/storage';
import {
  Container,
  ColumnImages,
  MainImageWrapper,
  MainImage,
  ZoomHint,
  ThumbnailStrip,
  Thumbnail,
  LightboxOverlay,
  LightboxImg,
  LightboxClose,
  LightboxNavBtn,
  Content,
  Title,
  Description,
  Price,
} from './styled';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { getCuadrosState } from 'selectors/cuadros';
import { getCuadros } from 'store/cuadros/actions';
import theme from 'config/theme';
import { optimizeCloudinaryUrl } from 'services/cloudinary';

export const DetailScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentCartCuadros = getCartCuadros();
  const cuadrosState = useAppSelector(getCuadrosState);
  const currentArrCartCuadros = currentCartCuadros?.split(',') || [];

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (!cuadrosState.data) dispatch(getCuadros());
  }, []);

  const cuadro = cuadrosState.data?.find(c => c.id === Number(id));

  const extraUrls = (cuadro?.extra_image_urls || []).filter(Boolean) as string[];

  const allImageSrcs: string[] = cuadro?.image_url
    ? [cuadro.image_url, ...extraUrls].map(url => optimizeCloudinaryUrl(url))
    : [];

  useEffect(() => {
    if (!cuadro && !cuadrosState.isLoading && cuadrosState.data) navigate('/');
  }, [cuadro]);

  useEffect(() => {
    document.body.style.overflow = lightboxOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxOpen]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowLeft') setSelectedIndex(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setSelectedIndex(i => Math.min(allImageSrcs.length - 1, i + 1));
      if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxOpen, allImageSrcs.length]);

  const addToCart = () => {
    setCartCuadros(currentCartCuadros ? `${currentCartCuadros},${id}` : `${id}`);
    window.location.reload();
  };

  const removeFromCart = () => {
    const arr = currentArrCartCuadros?.filter(item => item !== id);
    if (arr.toString()) setCartCuadros(arr.toString());
    else clearCartCuadros();
    window.location.reload();
  };

  return (
    <>
      {cuadrosState.isLoading && <Loader type="dots" />}
      <Header />
      <Container>
        <RowToGrid $switchToGrid={700}>
          <ColumnImages>
            <MainImageWrapper onClick={() => setLightboxOpen(true)}>
              <MainImage src={allImageSrcs[selectedIndex]} alt={cuadro?.name || 'Cuadro'} />
              <ZoomHint>
                <Icon name={IconNames.Search} color={theme.colors.white} size={14} />
              </ZoomHint>
            </MainImageWrapper>

            {allImageSrcs.length > 1 && (
              <ThumbnailStrip>
                {allImageSrcs.map((src, i) => (
                  <Thumbnail
                    key={i}
                    $selected={i === selectedIndex}
                    src={src}
                    alt={`Vista ${i + 1}`}
                    onClick={() => setSelectedIndex(i)}
                  />
                ))}
              </ThumbnailStrip>
            )}
          </ColumnImages>

          <Content>
            {cuadro && (
              <>
                <Title>{cuadro.name}</Title>
                <Spacing $size={12} />
                <Description dangerouslySetInnerHTML={{ __html: cuadro.description }} />
                <Spacing $size={8} />
                <Price>{cuadro.price}</Price>
                <Spacing $size={24} />
                {!currentArrCartCuadros.includes(`${id}`) ? (
                  <Button onClick={addToCart}>Agregar al Carrito</Button>
                ) : (
                  <Button onClick={removeFromCart}>Sacar del Carrito</Button>
                )}
              </>
            )}
          </Content>
        </RowToGrid>
      </Container>
      <Footer />

      <AnimatePresence>
        {lightboxOpen && (
          <LightboxOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setLightboxOpen(false)}
          >
            <LightboxImg
              src={allImageSrcs[selectedIndex]}
              alt={cuadro?.name || 'Cuadro'}
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
            />

            {allImageSrcs.length > 1 && (
              <>
                <LightboxNavBtn
                  $side="left"
                  disabled={selectedIndex === 0}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedIndex(i => Math.max(0, i - 1));
                  }}
                >
                  <Icon name={IconNames.ArrowLeft} color={theme.colors.white} size={20} />
                </LightboxNavBtn>
                <LightboxNavBtn
                  $side="right"
                  disabled={selectedIndex === allImageSrcs.length - 1}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedIndex(i => Math.min(allImageSrcs.length - 1, i + 1));
                  }}
                >
                  <Icon name={IconNames.ChevronRight} color={theme.colors.white} size={20} />
                </LightboxNavBtn>
              </>
            )}

            <LightboxClose
              onClick={e => {
                e.stopPropagation();
                setLightboxOpen(false);
              }}
            >
              <Icon name={IconNames.XClose} color={theme.colors.white} size={18} />
            </LightboxClose>
          </LightboxOverlay>
        )}
      </AnimatePresence>
    </>
  );
};
